// src/pages/SettingDisplayCollateral.js
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import AppContext from '../contexts/AppContext';
import { Button, Input, Modal, Switch, message, Radio } from 'antd';
import { FaDownload } from 'react-icons/fa';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { QRCodeCanvas } from 'qrcode.react';
import FloatingFooter from '../components/utils/FloatingFooter';

dayjs.extend(utc);
dayjs.extend(timezone);

const SettingDisplayCollateral = () => {
  const { selectedRole, token, userInfo } = useContext(AppContext);
  const venueList = userInfo?.roleInfo?.venue || [];
  const currentVenue = venueList.find(v => v.id === selectedRole?.id);

  const posterCanvasRef = useRef(null);     // 主画�?
  const qrCanvasWrapperRef = useRef(null);  // 隐藏 QR 容器
  const [qrDataUrl, setQrDataUrl] = useState('');

  const [copied, setCopied] = useState(false);//剪贴板操�?

  // 默认 displayCollateral
  const collateralDefaults = useMemo(
    () => ({
      title: 'GUEST INFORMATION',
      description: 'Check your compendium',
      color: '#000000',
      showQRUrl: true,
      posterSize: 'A4', // A4 | A6 | A6SQUARE | KEYTAG
    }),
    []
  );

  const [collateral, setCollateral] = useState(collateralDefaults);
  const [initialCollateral, setInitialCollateral] = useState(collateralDefaults);
  const [logoImage, setLogoImage] = useState('');
  const [saving, setSaving] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  // const isPublished = currentVenue?.actionLogs?.some(log => log.name === 'published');
  const isPublished = true; // 永远已发布，隐藏发布逻辑

  const hasChanges = useMemo(
    () => JSON.stringify(collateral) !== JSON.stringify(initialCollateral),
    [collateral, initialCollateral]
  );

  const posterSize = collateral?.posterSize || 'A4';

  // 预览宽高�?
  const posterAspect = useMemo(() => {
    switch (posterSize) {
      case 'A6':
        return 1240 / 1748;
      case 'A6SQUARE':
        return 1;
      case 'KEYTAG':
        return 2360 / 700;
      case 'A4':
      default:
        return 2480 / 3508;
    }
  }, [posterSize]);

  // 生成站点 URL
  const dynamicURL = useMemo(() => {
    const venueId = selectedRole?.id || '';
    const hasUppercase = /[A-Z]/.test(venueId);
    return hasUppercase
      ? `https://venue.platypus360.com/?venue_id=${venueId}`
      : `https://${venueId}.platypus360.com`;
  }, [selectedRole]);

  // 初始化展示内�?& Logo
  useEffect(() => {
    const mergedCollateral = {
      ...collateralDefaults,
      ...(currentVenue?.displayCollateral || {}),
    };
    setCollateral(mergedCollateral);
    setInitialCollateral(mergedCollateral);
    setLogoImage(currentVenue?.landing?.logoImage || '');
  }, [currentVenue, collateralDefaults]);

  // 抓取隐藏 QR 画布�?DataURL（带重试，避免首帧还没渲染好�?
  useEffect(() => {
    let cancelled = false;
    let retryCount = 0;
    const maxRetry = 40;

    const tryCaptureQr = () => {
      if (cancelled) return;

      const qrCanvas = qrCanvasWrapperRef.current?.querySelector('canvas');
      if (qrCanvas) {
        try {
          const dataUrl = qrCanvas.toDataURL('image/png');
          setQrDataUrl(dataUrl);
        } catch (err) {
          console.error('QR toDataURL failed:', err);
        }
      } else if (retryCount < maxRetry) {
        retryCount += 1;
        setTimeout(tryCaptureQr, 100);
      }
    };

    setQrDataUrl('');
    if (dynamicURL) {
      tryCaptureQr();
    }

    return () => {
      cancelled = true;
    };
  }, [dynamicURL]);

  // 工具：加载图片（用于 QR / Logo），包含跨域失败兜底
  const loadImageElement = useCallback((src, options = {}) =>
    new Promise((resolve, reject) => {
      const { crossOrigin = 'anonymous', timeoutMs = 10000 } = options;
      const img = new Image();
      let timeoutId = null;

      if (crossOrigin) {
        img.crossOrigin = crossOrigin;
      }
      img.referrerPolicy = 'no-referrer';

      timeoutId = setTimeout(() => {
        reject(new Error(`Image load timeout: ${src}`));
      }, timeoutMs);

      img.onload = () => {
        clearTimeout(timeoutId);
        resolve(img);
      };

      img.onerror = err => {
        clearTimeout(timeoutId);
        reject(err);
      };

      img.src = src;
    }), []);

  const loadImage = useCallback(async src => {
    if (!src) throw new Error('Empty src');

    try {
      return await loadImageElement(src);
    } catch (firstErr) {
      console.warn('Image direct load failed, trying fetch/blob fallback:', src, firstErr);
      const resp = await fetch(src, { mode: 'cors', cache: 'no-store' });
      if (!resp.ok) {
        throw new Error(`Image fetch failed: ${resp.status}`);
      }
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      try {
        return await loadImageElement(objectUrl, { crossOrigin: null });
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    }
  }, [loadImageElement]);

  // 工具：居中多行文�?
  const drawWrappedText = (
    ctx,
    text,
    x,
    startY,
    maxWidth,
    lineHeight,
    font = "48px 'Inter', sans-serif",
    color = '#111827'
  ) => {
    ctx.save();
    ctx.fillStyle = color;
    ctx.font = font;
    ctx.textAlign = 'center';

    const words = (text || '').split(' ');
    let line = '';
    let y = startY;

    for (let i = 0; i < words.length; i += 1) {
      const testLine = `${line}${words[i]} `;
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && i > 0) {
        ctx.fillText(line.trim(), x, y);
        line = `${words[i]} `;
        y += lineHeight;
      } else {
        line = testLine;
      }
    }

    if (line.trim()) {
      ctx.fillText(line.trim(), x, y);
    }

    ctx.restore();
  };

  // 绘制主海�?
  useEffect(() => {
    const canvas = posterCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width;
    let height;
    let qrSize;
    let qrY;
    let titleY;
    let footerHeight;
    let titleFont;
    let stdFontSize;
    let characterDecrease;
    let urlFont;
    let descFont;
    let urlLineHeight;
    let descLineHeight;
    let isKeytag = false;

    // 根据尺寸切配�?
    switch (posterSize) {
      case 'A6':
        width = 1240;
        height = 1748;
        qrSize = 500;
        qrY = 360;
        titleY = 240;
        footerHeight = 360;
        stdFontSize = 100;
        characterDecrease = 1;
        titleFont = "px 'Inter', sans-serif";
        urlFont = "32px 'Inter', sans-serif";
        descFont = "40px 'Inter', sans-serif";
        urlLineHeight = 46;
        descLineHeight = 48;
        break;

      case 'A6SQUARE':
        width = 1240;
        height = 1240;
        qrSize = 400;
        qrY = 240;
        titleY = 160;
        footerHeight = 240;
        stdFontSize = 100;
        characterDecrease = 1;
        titleFont = "px 'Inter', sans-serif";
        urlFont = "32px 'Inter', sans-serif";
        descFont = "40px 'Inter', sans-serif";
        urlLineHeight = 40;
        descLineHeight = 48;
        break;

      case 'KEYTAG':
        // 118mm x 70mm, 10px/mm；画布中并排展示 front/back
        width = 2360;
        height = 700;
        isKeytag = true;
        break;

      case 'A4':
      default:
        width = 2480;
        height = 3508;
        qrSize = 1200;
        qrY = 800;
        titleY = 500;
        footerHeight = 520;
        stdFontSize = 280;
        characterDecrease = 5;
        titleFont = "px 'Inter', sans-serif";
        urlFont = "48px 'Inter', sans-serif";
        descFont = "64px 'Inter', sans-serif";
        urlLineHeight = 64;
        descLineHeight = 70;
        break;
    }

    canvas.width = width;
    canvas.height = height;

    const draw = async () => {
      // 背景
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);

      if (isKeytag) {
        const panelW = width / 2;
        const panelH = height;
        const gapLineX = panelW;
        const colGap = 24;
        const colW = (panelW - 120 - colGap) / 2;
        const colH = panelH - 120;
        const leftColCenterY = panelH / 2;
        const frontLeftCenterX = 60 + colW / 2;
        const frontRightCenterX = frontLeftCenterX + colW + colGap;
        const backLeftCenterX = panelW + 60 + colW / 2;
        const backRightCenterX = backLeftCenterX + colW + colGap;

        const drawImageRotated = (img, cx, cy, w, h, angleDeg = 90) => {
          ctx.save();
          ctx.translate(cx, cy);
          ctx.rotate((angleDeg * Math.PI) / 180);
          ctx.drawImage(img, -w / 2, -h / 2, w, h);
          ctx.restore();
        };

        const drawWrappedTextRotated = (
          text,
          cx,
          cy,
          maxWidth,
          lineHeight,
          font,
          color,
          angleDeg = 90
        ) => {
          ctx.save();
          ctx.translate(cx, cy);
          ctx.rotate((angleDeg * Math.PI) / 180);
          drawWrappedText(
            ctx,
            text,
            0,
            0,
            maxWidth,
            lineHeight,
            font,
            color
          );
          ctx.restore();
        };

        // 分隔�?
        ctx.fillStyle = '#e5e7eb';
        ctx.fillRect(gapLineX - 1, 0, 2, panelH);

        // Front: 左标�?+ �?QR（均旋转 90 度）
        const qrSizeKeytag = Math.min(colW * 0.82, colH * 0.82);

        if (qrDataUrl) {
          try {
            const qrImg = await loadImage(qrDataUrl);
            drawImageRotated(
              qrImg,
              frontRightCenterX,
              leftColCenterY,
              qrSizeKeytag,
              qrSizeKeytag,
              90
            );
          } catch (err) {
            console.error('Failed to draw keytag QR', err);
          }
        }

        drawWrappedTextRotated(
          collateral?.title || collateralDefaults.title,
          frontLeftCenterX,
          leftColCenterY,
          colH * 0.9,
          46,
          "bold 56px 'Inter', sans-serif",
          '#111827',
          -90
        );

        // Back: �?logo + �?description（均旋转 90 度）
        const logoAreaTop = 60;
        const logoAreaHeight = 260;

        if (logoImage) {
          try {
            const logo = await loadImage(logoImage);
            const maxLogoWidth = colW * 0.78;
            const maxLogoHeight = colH * 0.78;
            const scale = Math.min(maxLogoWidth / logo.width, maxLogoHeight / logo.height, 1);
            const logoW = logo.width * scale;
            const logoH = logo.height * scale;
            drawImageRotated(
              logo,
              backLeftCenterX,
              leftColCenterY,
              logoW,
              logoH,
              -90
            );
          } catch (err) {
            console.error('Failed to draw keytag logo', err);
            ctx.fillStyle = '#1f2937';
            ctx.font = "bold 34px 'Inter', sans-serif";
            ctx.textAlign = 'center';
            ctx.fillText('Add logo in Branding', backLeftCenterX, logoAreaTop + logoAreaHeight / 2);
          }
        } else {
          ctx.fillStyle = '#1f2937';
          ctx.font = "bold 34px 'Inter', sans-serif";
          ctx.textAlign = 'center';
          ctx.fillText('Add logo in Branding', backLeftCenterX, logoAreaTop + logoAreaHeight / 2);
        }

        drawWrappedTextRotated(
          collateral?.description || collateralDefaults.description,
          backRightCenterX,
          leftColCenterY,
          colH * 0.85,
          38,
          "40px 'Inter', sans-serif",
          '#111827',
          -90
        );

        return;
      }

      // 标题
      ctx.fillStyle = '#1f2937';
      ctx.font = 'bold ' + (stdFontSize - (collateral?.title || collateralDefaults.title).length * characterDecrease).toString() + titleFont;
      ctx.textAlign = 'center';
      ctx.fillText(collateral?.title || collateralDefaults.title, width / 2, titleY);

      // QR
      const qrX = (width - qrSize) / 2;
      if (qrDataUrl) {
        try {
          const qrImg = await loadImage(qrDataUrl);
          ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
        } catch (err) {
          console.error('Failed to draw QR', err);
        }
      }

      const contentWidth = width * 0.8;

      // URL（可选）
      if (collateral?.showQRUrl && dynamicURL) {
        drawWrappedText(
          ctx,
          dynamicURL,
          width / 2,
          qrY + qrSize + urlLineHeight * 1,
          contentWidth,
          urlLineHeight,
          urlFont,
          '#2563eb'
        );
      }

      // 描述
      const descStartY =
        qrY + qrSize + stdFontSize * 0.5 + (collateral?.showQRUrl ? urlLineHeight * 3 : urlLineHeight * 2);

      drawWrappedText(
        ctx,
        collateral?.description || collateralDefaults.description,
        width / 2,
        descStartY,
        contentWidth,
        descLineHeight,
        descFont,
        '#111827'
      );

      // 底部�?
      const footerY = height - footerHeight;
      ctx.fillStyle = collateral?.color || collateralDefaults.color;
      ctx.fillRect(0, footerY, width, footerHeight);

      // Logo
      let logoRendered = false;
      if (logoImage) {
        try {
          const logo = await loadImage(logoImage);
          const maxLogoWidth = width * 0.6;
          const maxLogoHeight = footerHeight * 0.7;
          const scale = Math.min(maxLogoWidth / logo.width, maxLogoHeight / logo.height, 1);
          const logoW = logo.width * scale;
          const logoH = logo.height * scale;
          const logoX = (width - logoW) / 2;
          const logoY = footerY + (footerHeight - logoH) / 2;
          ctx.drawImage(logo, logoX, logoY, logoW, logoH);
          logoRendered = true;
        } catch (err) {
          console.error('Failed to draw logo', err);
        }
      }

      // Logo 加载失败或不存在时的占位
      if (!logoRendered) {
        ctx.fillStyle = '#1f2937';
        ctx.font = "bold 48px 'Inter', sans-serif";
        ctx.textAlign = 'center';
        ctx.fillText(
          'Add your logo in Branding settings',
          width / 2,
          footerY + footerHeight / 2
        );
      }
    };

    draw();
  }, [
    collateral,
    collateralDefaults,
    qrDataUrl,
    logoImage,
    loadImage,
    dynamicURL,
    posterSize,
  ]);

  // �?venue 角色
  if (selectedRole?.type !== 'venue') {
    return (
      <div className="w-[90%] max-w-7xl mx-auto mt-20">
        <p className="text-center text-gray-500">
          Display collateral is only available for venues.
        </p>
      </div>
    );
  }

  const handleConfirmPublish = () => {
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const venueId = selectedRole?.id;
      const API = process.env.REACT_APP_API_BASE_URL;
      const now = dayjs().utcOffset(10).format();

      const res = await fetch(`${API}/root/venue/${venueId}/update-actionlog`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: 'published',
          datetime: now,
          period: 365,
        }),
      });

      if (!res.ok) throw new Error('Publish failed');

      await res.json();
      message.success('Venue has been published.');
      window.location.reload();
    } catch (err) {
      console.error('Publish error:', err);
      message.error('Failed to publish venue.');
    } finally {
      setModalVisible(false);
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
  };

  // 保存 displayCollateral（底�?FloatingFooter 用）
  const handleSave = async () => {
    setSaving(true);
    try {
      // 中文注释：保存时剔除 posterSize
      const { posterSize, ...rest } = collateral;
      const body = { displayCollateral: rest };

      const resp = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/private/venue/${selectedRole.id}/property/update`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        }
      );

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || resp.statusText);
      }

      message.success('Display collateral saved.');
      setInitialCollateral(collateral);
    } catch (err) {
      console.error('Save display collateral error', err);
      message.error('Failed to save display collateral.');
    } finally {
      setSaving(false);
    }
  };


  // 下载海报
  const handleDownloadCanvas = () => {
    const canvas = posterCanvasRef.current;
    if (!canvas) return;

    try {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${collateral?.title || 'display-collateral'}.png`;
      link.click();
    } catch (err) {
      console.error('Download poster failed:', err);
      message.error(
        'Failed to download poster. Please check the logo image CORS configuration.'
      );
    }
  };

  const updateCollateral = (key, value) => {
    setCollateral(prev => ({ ...prev, [key]: value }));
  };

  /* 点击链接 �?复制到剪贴板 */
  const copyLink = () => {
    navigator.clipboard.writeText(dynamicURL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="w-[90%] max-w-7xl mx-auto mb-20">
      {!isPublished ? (
        <div className="flex justify-center items-center">
          <div className="mt-4 bg-white shadow-md rounded-lg p-6 text-center w-full py-[20rem]">
            <p className="text-gray-700 mb-8">
              This venue is not published yet. QR code and link will only be visible after publishing.
            </p>
            <Button type="primary" onClick={handleConfirmPublish}>
              Confirm to Publish
            </Button>

            <Modal
              title="Confirm Publish"
              open={modalVisible}
              onOk={handleModalOk}
              onCancel={handleModalCancel}
              okText="Yes, Publish"
              cancelText="Cancel"
            >
              <p>Are you sure you want to mark this venue as published?</p>
            </Modal>
          </div>
        </div>
      ) : (
        <div className="mt-4 bg-white shadow-md rounded-lg p-6 space-y-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="space-y-5">
              <div className="rounded-[22px] border border-[#e7ebf8] bg-[linear-gradient(180deg,#ffffff_0%,#fbfcff_100%)] p-5 shadow-[0_12px_26px_rgba(30,52,120,0.05)]">
                <div className="space-y-2">
                  <label className="flex items-baseline gap-2 text-sm font-semibold text-[#334155]">
                    <span>Poster title</span>
                    <span className="rounded-full bg-[#f2f5ff] px-2 py-0.5 text-[11px] font-medium text-[#7a88a6]">
                      {collateral?.title.length}/32
                    </span>
                  </label>
                  <Input
                    value={collateral?.title}
                    onChange={e => updateCollateral('title', e.target.value)}
                    placeholder="Enter a headline for your poster"
                    maxLength={32}
                    className="rounded-xl border-[#dbe3f5] shadow-none"
                  />
                </div>

                <div className="mt-5 space-y-2">
                  <label className="flex items-baseline gap-2 text-sm font-semibold text-[#334155]">
                    <span>Description</span>
                    <span className="rounded-full bg-[#f2f5ff] px-2 py-0.5 text-[11px] font-medium text-[#7a88a6]">
                      {collateral?.description.length}/128
                    </span>
                  </label>
                  <Input.TextArea
                    rows={4}
                    value={collateral?.description}
                    onChange={e =>
                      updateCollateral('description', e.target.value)
                    }
                    placeholder="Explain what guests should do after scanning the QR"
                    maxLength={128}
                    className="rounded-[18px] border-[#dbe3f5] shadow-none"
                  />
                </div>

                <div className="mt-5 flex flex-col gap-1.5">
                  <div>
                    <p className="text-sm font-semibold text-[#334155]">
                      Poster size
                    </p>
                    <p className="mt-0.5 text-[11px] leading-5 text-[#7a88a6]">
                      Choose the size for your printed collateral.
                    </p>
                  </div>
                  <Radio.Group
                    value={posterSize}
                    onChange={e =>
                      updateCollateral('posterSize', e.target.value)
                    }
                    className="display-collateral-size-group"
                  >
                    <Radio.Button value="A4">A4</Radio.Button>
                    <Radio.Button value="A6">A6</Radio.Button>
                    <Radio.Button value="A6SQUARE">A6 Square</Radio.Button>
                    <Radio.Button value="KEYTAG">Keytag</Radio.Button>
                  </Radio.Group>
                </div>

                <div className="mt-5 border-t border-[#e8edf9] pt-5 space-y-4">
                  <div className="flex items-center justify-between rounded-[18px] bg-white/80 px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-[#334155]">
                        Logo background colour
                      </p>
                      <p className="text-xs text-[#7a88a6]">
                        Affects the colour of the logo footer bar.
                      </p>
                    </div>
                    <input
                      type="color"
                      className="h-11 w-20 rounded-xl border border-[#dbe3f5] bg-white"
                      value={collateral?.color || '#ffffff'}
                      onChange={e => updateCollateral('color', e.target.value)}
                    />
                  </div>

                  <div className="flex items-center justify-between rounded-[18px] bg-white/80 px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-[#334155]">
                        Show QR URL under the code
                      </p>
                      <p className="text-xs text-[#7a88a6]">
                        Toggle whether the URL is printed below the QR code.
                      </p>
                    </div>
                    <Switch
                      checked={Boolean(collateral?.showQRUrl)}
                      onChange={checked =>
                        updateCollateral('showQRUrl', checked)
                      }
                    />
                  </div>

                  <div className="flex flex-col items-center pt-1">
                    <div className="w-full break-all rounded-[18px] border border-[#e3e9f8] bg-white px-5 py-4 shadow-[0_12px_24px_rgba(15,23,42,0.05)]">
                      <p className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8a97b4]">
                        Website URL
                      </p>
                      <p className="mt-2 text-[#52607d]">
                        <span
                          onClick={copyLink}
                          className="cursor-pointer text-[15px] font-medium text-[#2557d6] hover:underline"
                          title="Click to copy"
                        >
                          {dynamicURL}
                        </span>
                        {copied && (
                          <span className="ml-2 text-sm text-green-600">
                            Copied!
                          </span>
                        )}
                      </p>

                      <a
                        href={dynamicURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex w-44 items-center justify-center rounded-xl bg-[#4a5cff] px-10 py-2.5 text-center font-medium text-white transition-all duration-200 hover:-translate-y-[1px] hover:bg-[#4154f5] hover:shadow-[0_10px_20px_rgba(74,92,255,0.16)]"
                      >
                        Open Link
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-5">
              <div className="rounded-[24px] border border-[#e7ebf8] bg-[linear-gradient(180deg,#ffffff_0%,#f8faff_100%)] p-4 shadow-[0_16px_34px_rgba(30,52,120,0.08)]">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8a97b4]">
                      Live Poster Preview
                    </p>
                    <p className="mt-1 text-[14px] font-medium text-[#52607d]">
                      Download-ready layout preview
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleDownloadCanvas}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-[#d9e3ff] bg-white text-[#4a5cff] transition-all duration-200 hover:-translate-y-[1px] hover:border-[#c8d3fb] hover:bg-[#eef2ff] hover:shadow-[0_10px_20px_rgba(74,92,255,0.12)]"
                    aria-label="Download poster"
                  >
                    <FaDownload className="h-5 w-5" />
                  </button>
                </div>

                <div className="overflow-hidden rounded-[20px] border border-[#e4e9f7] bg-white p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
                  <canvas
                    ref={posterCanvasRef}
                    className="w-full h-auto shadow-sm"
                    style={{ aspectRatio: posterAspect }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* QR 独立区域
          <div className="border-t pt-6">
            <QRCodeGenerator selectedRole={selectedRole} />
          </div> */}

          {/* 隐藏�?QR 画布，仅用于合成主海�?*/}
          <div className="hidden" ref={qrCanvasWrapperRef}>
            <QRCodeCanvas
              value={dynamicURL}
              size={600}
              bgColor="#ffffff"
              fgColor="#000000"
            />
          </div>
        </div>
      )}

      {isPublished && (
        <FloatingFooter
          hasChanges={hasChanges}
          onSave={handleSave}
          saveLabel="Save"
          loading={saving}
        />
      )}
    </div>
  );
};

export default SettingDisplayCollateral;

